/**
 * Vera module catalog DATA: the pure list, split from moduleCatalog.ts on
 * 2026-08-08 after it took down the reports-scheduler Function App: admin
 * config defaults (entities/admin-config/model/defaults.ts) import the
 * catalog, server bundles (the scheduler's fetchers) import those defaults,
 * and the old single file chained on into AuthProvider -> config/env, whose
 * top-level `import.meta.env.PROD` read crashes any CJS bundle at load.
 *
 * RULE: nothing in this file may import React providers, hooks, or anything
 * that reads import.meta. Icons and color tokens are fine (plain values).
 * The hook side (useAccessibleModules) lives in moduleCatalog.ts, which
 * re-exports everything here, so UI imports are unchanged.
 */
import {
  PeopleRegular,
  BookTemplateRegular,
  ChartMultipleRegular,
  DocumentBulletListRegular,
  ReceiptMoneyRegular,
  MoneyRegular,
  DataPieRegular,
  WrenchScrewdriverRegular,
  LightbulbRegular,
  MegaphoneRegular,
  ShieldKeyholeRegular,
  HatGraduationRegular,
  TargetArrowRegular,
  ConferenceRoomRegular,
  ShareRegular,
  PillRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { veracityColors } from '../tokens';
import { hasGlobalAdmin } from '../../auth/roles';
import { canManageDemoPages } from '../../auth/capabilities';

export type ModuleStatus = 'live' | 'soon';

/**
 * Launcher sections, in render order (Sasha, 2026-08-08). The switcher keeps
 * a flat list but MODULE_CATALOG is ordered by category so both surfaces
 * read the same way. 'oneveracity' holds the company-wide surfaces every
 * employee touches (Ideas, Academy, Portfolio).
 *
 * Each category carries ONE approved brand color, used on the launcher's
 * SECTION LABEL only. Tiles keep their own per-module `accent`, which is also
 * what the AppShell header of each app uses, so a tile matches the app it
 * opens into. Revised 2026-08-08 (Sasha): the first pass colored every tile in
 * a section with the category color and read as one flat block. There is no
 * compliant way to vary hue within a category, because the brand guide allows
 * only 10% opacity steps on secondaries and none at all on Veracity Orange, so
 * per-module accents are the variation.
 *
 * Per brand guide 4.5 (approved combinations): Orange / Charcoal / Plum /
 * Dusk Blue / Clarity Green are dark backgrounds and take a light foreground;
 * Daybreak Amber, Morning Coral and Horizon Blue are LIGHT and require the
 * Midnight Charcoal foreground via `accentForeground`.
 *
 * These are the built-in defaults: admins can reorder, relabel, recolor, and
 * reassign modules via Catalog > Configuration > Launcher Categories
 * (admin-config key 'shell.launcherCategories').
 */
export const MODULE_CATEGORIES: readonly {
  id: string;
  label: string;
  color: string;
  foreground?: string;
}[] = [
  { id: 'sales', label: 'Sales', color: veracityColors.twilightPlum },
  { id: 'operations', label: 'Operations', color: veracityColors.veracityOrange },
  // Rx and Claims Analysis, split out of Operations (Sasha, 2026-09-27).
  {
    id: 'analytics',
    label: 'Analytics',
    color: veracityColors.horizonBlue,
    foreground: veracityColors.midnightCharcoal,
  },
  {
    id: 'oneveracity',
    label: 'OneVeracity',
    color: veracityColors.daybreakAmber,
    foreground: veracityColors.midnightCharcoal,
  },
  { id: 'tools', label: 'Tools', color: veracityColors.duskBlue },
];
export type VeraModuleCategory = 'sales' | 'operations' | 'analytics' | 'oneveracity' | 'tools';

export interface ModuleCatalogEntry {
  id: string;
  rolePrefix: string;
  /** Display name (launcher tile heading + switcher label). */
  name: string;
  /** Longer blurb shown on the launcher tiles. */
  description: string;
  /** Route to navigate to. `#` for `soon` modules. */
  path: string;
  icon: FluentIcon;
  status: ModuleStatus;
  /** Launcher section this module renders under. */
  category: VeraModuleCategory;
  /** Accent color for the icon circle. */
  accent: string;
  /**
   * Foreground for icons rendered on the accent color. Defaults to white;
   * light accents (Daybreak Amber) require Midnight Charcoal per the brand
   * guide's approved color combinations (§4.5).
   */
  accentForeground?: string;
  /** Hide from the launcher and switcher without removing the entry. */
  hidden?: boolean;
  /**
   * Module is open to every authenticated employee — no module role needed.
   * (Ideas: the submission bar is meant to be as low as the access bar.)
   */
  openToAll?: boolean;
  /**
   * Entry is a TOOL, not a module: it belongs in the header switcher but the
   * launcher renders it in its own "Tools" section, so the Modules grid skips
   * it. (C13: Administration is reachable from the switcher, not just home.)
   */
  tool?: boolean;
  /**
   * Custom access rule for entries whose gate isn't a `<Prefix>.*` module role
   * (Administration). When set, it fully replaces the default role check.
   */
  accessWhen?: (roles: readonly string[]) => boolean;
}

// Ordered by category (MODULE_CATEGORIES) so the flat switcher list reads
// the same way as the sectioned launcher.
export const MODULE_CATALOG: ModuleCatalogEntry[] = [
  // ── Sales ──
  {
    id: 'sales',
    rolePrefix: 'Sales',
    name: 'Sales',
    description: 'Pipeline tracking, broker scorecards, analytics',
    path: '/sales',
    icon: ChartMultipleRegular,
    status: 'live',
    category: 'sales',
    accent: veracityColors.midnightCharcoal,
  },
  {
    id: 'marketing',
    rolePrefix: 'Marketing',
    name: 'Marketing',
    description: 'Braze sync status — contacts & leads flowing from Dynamics',
    path: '/marketing',
    icon: MegaphoneRegular,
    status: 'live',
    category: 'sales',
    accent: veracityColors.horizonBlue,
    accentForeground: veracityColors.midnightCharcoal,
  },
  {
    // Renamed RFP -> Proposal 2026-08-07 (Sasha). The DISPLAY name and the
    // route moved; the identifiers did not: `id`, `rolePrefix`, the Rfp.*
    // role values, /api/rfp, the pg schema, and @vera/rfp all stay, because
    // role grants are keyed by the role value string and renaming those
    // orphans every existing grant.
    id: 'rfp',
    rolePrefix: 'Rfp',
    name: 'Proposal',
    description: 'Response library, AI-assisted drafting, win/loss tracking',
    path: '/proposal',
    icon: DocumentBulletListRegular,
    status: 'live',
    category: 'sales',
    accent: veracityColors.clarityGreen,
  },

  // ── Operations ──
  {
    id: 'ops',
    rolePrefix: 'Ops',
    name: 'Operations',
    description: 'Group enrollment, plan year management, renewals',
    path: '/ops',
    icon: PeopleRegular,
    status: 'live',
    category: 'operations',
    accent: veracityColors.veracityOrange,
  },
  {
    id: 'maple',
    rolePrefix: 'Maple',
    name: 'Maple',
    description: 'Pharmacy claim billing & payment — bill, collect, pay',
    path: '/maple',
    icon: ReceiptMoneyRegular,
    status: 'live',
    category: 'operations',
    accent: veracityColors.midnightCharcoal,
  },
  {
    id: 'finance',
    rolePrefix: 'Finance',
    name: 'Finance',
    description: 'Financial monitoring, stop-loss renewals, fee registry',
    path: '/finance',
    icon: MoneyRegular,
    status: 'live',
    // "For now" (Sasha, 8/08): Finance sits under Operations until it earns
    // its own section.
    category: 'operations',
    accent: veracityColors.clarityGreen,
  },
  {
    id: 'implementation',
    rolePrefix: 'Implementation',
    name: 'Implementation',
    description: 'Project tracking, section library, task manifests',
    path: '#',
    icon: WrenchScrewdriverRegular,
    status: 'soon',
    category: 'operations',
    accent: veracityColors.duskBlue,
    hidden: true,
  },

  // ── OneVeracity (company-wide surfaces every employee touches) ──
  {
    id: 'ideas',
    rolePrefix: 'Ideas',
    name: 'Ideas',
    description: 'Got a bright idea? Share it and watch it ship',
    path: '/ideas',
    icon: LightbulbRegular,
    status: 'live',
    category: 'oneveracity',
    accent: veracityColors.daybreakAmber,
    accentForeground: veracityColors.midnightCharcoal,
    openToAll: true,
  },
  {
    id: 'academy',
    rolePrefix: 'Academy',
    name: 'Academy',
    description: 'Know: SOPs, playbooks, the glossary. Learn: courses, quizzes, certificates',
    path: '/academy',
    icon: HatGraduationRegular,
    status: 'live',
    category: 'oneveracity',
    accent: veracityColors.morningCoral,
    // Brand guide 4.5: Morning Coral is a LIGHT background and takes the
    // Midnight Charcoal foreground, same as Daybreak Amber and Horizon Blue.
    // This was missing and went unnoticed only because the launcher was
    // overriding every tile with its category color; the glyph would have
    // rendered white on coral the moment tiles used their own accents.
    accentForeground: veracityColors.midnightCharcoal,
    // Open to every authenticated Veraciter (Ideas precedent, PLAN 12.2):
    // restricted content is handled per-item, not by gating the module.
    openToAll: true,
  },
  {
    id: 'portfolio',
    rolePrefix: 'Portfolio',
    name: 'Portfolio',
    description: 'The initiative register: every significant project, its owner, health, and blockers',
    path: '/portfolio',
    icon: TargetArrowRegular,
    status: 'live',
    category: 'oneveracity',
    // Twilight Plum: the last unclaimed brand secondary in this catalog.
    accent: veracityColors.twilightPlum,
    // Role-gated (2026-08-08, Sasha): Portfolio.User to read; owners/leads
    // need it too. NOT openToAll, unlike Ideas/Academy - the register names
    // company projects leadership may not want broadcast.
  },
  {
    id: 'share',
    rolePrefix: 'Share',
    name: 'Share',
    description: 'Dashboards and reports published to named people, sensitive content included',
    path: '/share',
    icon: ShareRegular,
    status: 'live',
    category: 'oneveracity',
    // Dusk Blue, reused: every brand secondary was claimed once Portfolio took
    // Twilight Plum. Reuse is already the norm here (Catalog and Analytics
    // share this one), and the module colours are locked per the UX guide.
    accent: veracityColors.duskBlue,
    // Role-gated by design and NOT openToAll: Share.User is gate 1, the whole
    // point being to limit who can see the module exists. Files may carry PHI.
  },
  {
    id: 'desk',
    rolePrefix: 'Desk',
    // Working name (Sasha 10/28). Renaming is this line plus the module's
    // appTitle; the code says desk everywhere else.
    name: 'Front Desk',
    description: 'Ask any team for anything, and follow it until it is done',
    path: '/desk',
    icon: ConferenceRoomRegular,
    status: 'live',
    category: 'oneveracity',
    // Daybreak Amber, shared with Ideas on purpose: Front Desk absorbs Ideas.
    accent: veracityColors.daybreakAmber,
    accentForeground: veracityColors.midnightCharcoal,
    // PILOT: role-gated (Desk.User) until launch, then openToAll like Ideas.
  },
  {
    id: 'rx',
    rolePrefix: 'Rx',
    // Shown as "Rx Savings Analysis" (Sasha, 9/27), display only: id, route,
    // role values and API are unchanged.
    name: 'Rx Savings Analysis',
    description: 'Pharmacy savings evidence and prospect ROI analysis',
    path: '/rx',
    icon: PillRegular,
    status: 'live',
    // Operations, not oneveracity (Sasha, 8/13): Rx is the pharmacy team's
    // working surface, not a company-wide one. Moved to its own Analytics
    // section with Claims Analysis (Sasha, 9/27).
    category: 'analytics',
    // Clarity Green, reused (Catalog and Finance already share tokens): the
    // module colours are locked per the UX guide and every secondary is taken.
    accent: veracityColors.clarityGreen,
    // Role-gated, NOT openToAll: the evidence layer is aggregate, but a
    // prospect run carries that prospect's claims.
  },
  {
    // Was the hidden 'soon' placeholder; became real 9/04 when Vera Clinical
    // (built 9/03-9/04) was renamed Vera Analytics pre-deploy and took over
    // this reserved id. Claims + eligibility today; the cross-module
    // reporting ambition stays the direction.
    id: 'analytics',
    rolePrefix: 'Analytics',
    // Shown as "Claims Analysis" (Sasha, 9/27). Display only, like the
    // RFP -> Proposal rename: the id, route, role values and API are unchanged.
    name: 'Claims Analysis',
    description: 'Claims & eligibility exploration over the data platform',
    path: '/analytics',
    icon: DataPieRegular,
    status: 'live',
    category: 'analytics',
    // Dusk Blue, reused (Catalog has it): a dark secondary, light foreground
    // per s4.5. Same reuse precedent as Clarity Green above.
    accent: veracityColors.duskBlue,
    // Role-gated, NOT openToAll: dashboards aggregate, but the claim-detail
    // drill is member-level PHI.
  },

  // ── Tools ──
  {
    id: 'catalog',
    rolePrefix: 'Catalog',
    name: 'Catalog',
    description: 'Solutions, services, templates, managed lists',
    path: '/catalog',
    icon: BookTemplateRegular,
    status: 'live',
    category: 'tools',
    accent: veracityColors.duskBlue,
  },
  {
    // C13 (7/23 review): gated exactly like the launcher's Administration tile
    // (global admin, or a Demo Pages manager who lands on that tab only).
    id: 'admin',
    rolePrefix: 'Admin',
    name: 'Administration',
    description: 'Role assignments, user activity, audit log, demo pages',
    path: '/admin',
    icon: ShieldKeyholeRegular,
    status: 'live',
    category: 'tools',
    accent: veracityColors.midnightCharcoal,
    tool: true,
    accessWhen: (roles) => hasGlobalAdmin(roles) || canManageDemoPages(roles),
  },
];
